/**
 * Геометрия маршрутизации: точка в контуре, центроид юнита, ближайший узел сети,
 * пересечение отрезков, группировка «стопки» этажей одного здания. Общая база
 * для route-access / route-foreign / route-points — вынесена, чтобы каждый из
 * них укладывался в лимит 500 строк и не дублировал формулы.
 */
import { haversineM, type LonLat, projectOnSegment } from '@app/geo'
import type { Venue } from './types'

/** Узлы-двери/входы/рампы (kind) — для геом-фолбэка «нарисовано на контуре». */
export const DOOR_KINDS = new Set<string>(['door', 'entrance', 'dock'])

/** Точка внутри кольца (ray casting). */
export function pointInRing(pt: readonly number[], ring: readonly number[][]): boolean {
  const x = pt[0] as number
  const y = pt[1] as number
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i]?.[0] as number
    const yi = ring[i]?.[1] as number
    const xj = ring[j]?.[0] as number
    const yj = ring[j]?.[1] as number
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

/** Точка внутри любого из контуров (для отсечения «внутрь чужого здания»). */
export function pointInAnyRing(point: LonLat, rings: readonly number[][][]): boolean {
  for (const ring of rings) {
    if (pointInRing(point, ring)) return true
  }
  return false
}

/** Пересекаются ли отрезки p1p2 и p3p4 (знаки ориентации, без деления). */
export function segmentsCross(p1: LonLat, p2: LonLat, p3: number[], p4: number[]): boolean {
  const d = (a: readonly number[], b: readonly number[], c: readonly number[]): number =>
    ((b[0] as number) - (a[0] as number)) * ((c[1] as number) - (a[1] as number)) -
    ((b[1] as number) - (a[1] as number)) * ((c[0] as number) - (a[0] as number))
  const d1 = d(p3, p4, p1)
  const d2 = d(p3, p4, p2)
  const d3 = d(p1, p2, p3)
  const d4 = d(p1, p2, p4)
  return d1 * d2 < 0 && d3 * d4 < 0
}

/**
 * Прямое плечо «последних метров» пересекает чужое здание? Даже когда оба конца
 * снаружи, отрезок может срезать угол соседнего корпуса — на карте это читается
 * как «маршрут идёт сквозь здание» (фидбек владельца 27.07).
 */
export function segmentCrossesRings(a: LonLat, b: LonLat, rings: readonly number[][][]): boolean {
  for (const ring of rings) {
    for (let i = 0; i < ring.length - 1; i++) {
      const p3 = ring[i]
      const p4 = ring[i + 1]
      if (p3 && p4 && segmentsCross(a, b, p3, p4)) return true
    }
  }
  return false
}

/** Центроид внешнего кольца юнита (среднее вершин — хватает для радиуса). */
export function unitCentroid(unit: { geometry: { coordinates: number[][][] } }): LonLat | null {
  const ring = unit.geometry.coordinates[0]
  if (!ring || ring.length === 0) return null
  let lon = 0
  let lat = 0
  for (const pt of ring) {
    lon += pt[0] as number
    lat += pt[1] as number
  }
  return [lon / ring.length, lat / ring.length]
}

/** Ближайший узел графа (локальная копия route.ts::nearestNodeId). */
export function nearestAccessNodeId(venue: Venue, point: LonLat, level?: string): string | null {
  let best: { id: string; dist: number } | null = null
  for (const node of venue.network.nodes) {
    if (level !== undefined && node.level !== level) continue
    const dist = haversineM(node.point, point)
    if (!best || dist < best.dist) best = { id: node.id, dist }
  }
  if (!best && level !== undefined) return nearestAccessNodeId(venue, point)
  return best?.id ?? null
}

/** Ближайшая к точке `from` точка на контуре (ломаной) — проекция на рёбра. */
export function nearestPointOnRing(from: LonLat, ring: readonly number[][]): LonLat | null {
  let best: LonLat | null = null
  let bestD = Number.POSITIVE_INFINITY
  for (let i = 0; i < ring.length - 1; i++) {
    const a = ring[i]
    const b = ring[i + 1]
    if (!a || !b) continue
    const proj = projectOnSegment(
      from,
      [a[0] as number, a[1] as number],
      [b[0] as number, b[1] as number],
    )
    if (proj.distanceM < bestD) {
      bestD = proj.distanceM
      best = proj.point
    }
  }
  return best
}

/** Площадь кольца (shoelace, квадратные градусы) — только для СРАВНЕНИЯ. */
function ringAreaRaw(ring: readonly number[][]): number {
  let s = 0
  for (let i = 0; i < ring.length - 1; i++) {
    const a = ring[i]
    const b = ring[i + 1]
    if (!a || !b) continue
    s += (a[0] as number) * (b[1] as number) - (b[0] as number) * (a[1] as number)
  }
  return Math.abs(s / 2)
}

/**
 * Юнит, внутри контура которого лежит POI (тот же уровень) — САМЫЙ МАЛЕНЬКИЙ
 * из содержащих. Точка на полке лежит и в гондоле, и в оболочке этажа: раньше
 * побеждал первый по порядку файла — для демо-гипермаркета это была оболочка,
 * и маршрут «к шампуням на 2-м» вёл к уличной двери здания (баг 03.08.2026).
 * Самый специфичный контейнер чинит это без завязки на порядок секций.
 */
export function unitContaining(
  venue: Venue,
  poi: Venue['pois'][number],
): Venue['units'][number] | null {
  let best: Venue['units'][number] | null = null
  let bestArea = Number.POSITIVE_INFINITY
  for (const u of venue.units) {
    if (u.level !== poi.level) continue
    const ring = u.geometry.coordinates[0]
    if (!ring || !pointInRing(poi.point, ring)) continue
    const area = ringAreaRaw(ring)
    if (area < bestArea) {
      best = u
      bestArea = area
    }
  }
  return best
}

/** Здание, которому принадлежит цель (POI внутри контура); null — вне зданий. */
export function hostUnitOf(venue: Venue, targetId: string): Venue['units'][number] | null {
  const poi = venue.pois.find((p) => p.id === targetId)
  return poi ? unitContaining(venue, poi) : null
}

/**
 * Юниты ОДНОГО корпуса: тот же контур на разных этажах (редактор часто дублирует
 * footprint по уровням) — центроид одного лежит в контуре другого. Владелец видит
 * ОДНО здание с дверями; двери/рампы могут висеть на версии другого этажа.
 */
export function sameBuildingUnits(
  venue: Venue,
  unit: Venue['units'][number],
): Venue['units'][number][] {
  const ring = unit.geometry.coordinates[0]
  const c = unitCentroid(unit)
  const out = [unit]
  for (const u of venue.units) {
    if (u.id === unit.id) continue
    const r = u.geometry.coordinates[0]
    const uc = unitCentroid(u)
    if ((ring && uc && pointInRing(uc, ring)) || (r && c && pointInRing(c, r))) out.push(u)
  }
  return out
}
